import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import PasswordStrengthMeter, { scorePassword } from "@/components/PasswordStrengthMeter";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) return setErr("Password must be at least 8 characters.");
    if (password !== confirm) return setErr("Passwords do not match.");
    if (scorePassword(password).score <= 1) return setErr("Please pick a stronger password — try adding letters, numbers, and a symbol.");
    if (!token) return setErr("Missing reset token in the URL.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated. Please log in.");
      navigate("/login");
    } catch (e) {
      setErr(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain min-h-screen flex items-center justify-center px-6 py-12 relative">
      <div className="w-full max-w-md relative z-10">
        <Link to="/" className="flex items-center gap-3 mb-10 justify-center">
          <div className="w-10 h-10 rounded-full bg-rose-600 flex items-center justify-center text-lg">🐼</div>
          <span className="font-display text-2xl tracking-tight">My Panda X</span>
        </Link>
        <div className="card-surface p-8">
          <h1 className="font-display text-3xl mb-1">Set a new password.</h1>
          <p className="text-sm text-zinc-400 mb-8">Choose something you'll remember.</p>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">New password</Label>
              <Input
                data-testid="reset-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-black/40 border-white/10 rounded-none py-6 focus-visible:ring-rose-600"
              />
              <PasswordStrengthMeter password={password} testid="reset-password-strength" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Confirm password</Label>
              <Input
                data-testid="reset-password-confirm-input"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-2 bg-black/40 border-white/10 rounded-none py-6 focus-visible:ring-rose-600"
              />
            </div>
            {err && (
              <div className="text-sm text-rose-500 border-l-2 border-rose-500 pl-3">{err}</div>
            )}
            <Button
              data-testid="reset-submit-button"
              type="submit"
              disabled={busy}
              className="btn-crimson w-full rounded-none py-6 tracking-wide"
            >
              {busy ? "Saving…" : "Update password"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
